import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { type AccountInfoData } from "~/features/b2bAccountSelection/components/AccountSelectionTabelView/api/GetAccountList";
import { useBooleanState } from "~/common/hooks/useBooleanState";
import DropdownWithMultiSelect, {
  type MultiselectAccountValue,
} from "~/features/userAdministration/manageUsers/components/DropdownWithMultiselect/DropdownWithMultiSelect";
import { EMAIL_PATTERN } from "~/utils/constants";
import { makeAccountDataIntoMultiSelect } from "../helper/makeAccountData";
import { type EmailOrderQuoteData } from "../types";
import {
  userCanSetMyCompanyOrders,
  userCanSetMyCompanyQuotes,
} from "~/features/myAccount/dashboard/helper/permissions";
import { useTranslation } from "react-i18next";
import { Box, Button, Stack, TextField, Typography } from "~/common/mui";
import { Dialog, DialogActions, DialogContent, DialogHeader, DialogTitle } from "~/common/mui/Dialog/Dialog";
import DeleteIcon from "@mui/icons-material/Delete";

interface EmailOrderQuotePopupProps {
  availableAccounts?: AccountInfoData[];
  onSubmit?: (data: EmailOrderQuoteData) => void;
  onDelete?: (data: EmailOrderQuoteData) => void;
}

interface FormData {
  recipientEmail: string;
  orderAcknowledgement: string[];
  quoteAcknowledgement: string[];
}

const EMPTY_FORM: FormData = {
  recipientEmail: "",
  orderAcknowledgement: [],
  quoteAcknowledgement: [],
};

export const useEmailOrderQuotePopup = ({ availableAccounts, onSubmit, onDelete }: EmailOrderQuotePopupProps) => {
  const { setTrue: openPopup, setFalse: closePopup, val: isModalOpen } = useBooleanState(false);

  const [options, setOptions] = useState<MultiselectAccountValue[]>([]);
  const [originalData, setOriginalData] = useState<EmailOrderQuoteData | undefined>(undefined);

  const {
    control,
    getValues,
    formState: { errors },
    reset,
    watch,
  } = useForm<FormData>({
    mode: "onBlur",
    defaultValues: {
      ...EMPTY_FORM,
    },
  });

  const { t } = useTranslation();

  // manage popup open
  const open = (emailOrderQuoteData?: EmailOrderQuoteData) => {
    if (emailOrderQuoteData) {
      // EDIT MODE
      reset({
        recipientEmail: emailOrderQuoteData.recipientEmail,
        orderAcknowledgement: emailOrderQuoteData.orderAcknowledgement.map((item) => item.id),
        quoteAcknowledgement: emailOrderQuoteData.quoteAcknowledgement.map((item) => item.id),
      });
      setOriginalData(emailOrderQuoteData);
    } else {
      // NEW MODE
      reset({
        ...EMPTY_FORM,
      });
      setOriginalData(undefined);
    }
    setOptions(makeAccountDataIntoMultiSelect({ list: availableAccounts ?? [], email: [] }));
    openPopup();
  };

  // reset on close
  const close = () => {
    closePopup();
  };

  const recipientEmail = watch("recipientEmail");
  const isDisabled = Object.keys(errors).length > 0 || !recipientEmail;

  const errorMessage = "error.invalidEmail";
  const isEditMode = !!originalData;

  const modal = (
    <Dialog open={isModalOpen} onClose={close} maxWidth="desktop-m">
      <DialogHeader onClose={close}>
        <DialogTitle sx={{ p: 0 }}>
          {t(isEditMode ? "page.orderQuoteSettings.modal.title.edit" : "page.orderQuoteSettings.modal.title.add")}
        </DialogTitle>
      </DialogHeader>
      <DialogContent>
        <Stack spacing={2}>
          <Controller
            name="recipientEmail"
            control={control}
            render={({ field: { value, onChange, onBlur } }) => (
              <TextField
                value={value}
                onChange={onChange}
                onBlur={onBlur}
                label={t("page.orderQuoteSettings.modal.label.email")}
                error={!!errors.recipientEmail}
                helperText={errors.recipientEmail ? t(errors.recipientEmail.message!) : undefined}
                disabled={isEditMode}
                fullWidth
              />
            )}
            rules={{
              required: { value: true, message: errorMessage },
              pattern: { value: EMAIL_PATTERN, message: errorMessage },
            }}
          />
          <Typography sx={{ mt: 2 }}>{t("page.orderQuoteSettings.modal.label.copy")}</Typography>
          <Stack gap={3}>
            {userCanSetMyCompanyOrders && (
              <Stack spacing={1}>
                <Typography>{t("page.orderQuoteSettings.modal.label.order")}</Typography>
                <Controller
                  name="orderAcknowledgement"
                  control={control}
                  render={({ field }) => (
                    <DropdownWithMultiSelect
                      label={t("page.orderQuoteSettings.modal.label.account")}
                      items={options}
                      data-test-id="email-order-popup-quoiteAccountMultiselect"
                      {...field}
                    />
                  )}
                />
              </Stack>
            )}
            {userCanSetMyCompanyQuotes && (
              <Stack spacing={1}>
                <Typography>{t("page.orderQuoteSettings.modal.label.quote")}</Typography>
                <Controller
                  name="quoteAcknowledgement"
                  control={control}
                  render={({ field }) => (
                    <DropdownWithMultiSelect
                      label={t("page.orderQuoteSettings.modal.label.account")}
                      items={options}
                      data-test-id="email-order-popup-quoiteAccountMultiselect"
                      {...field}
                    />
                  )}
                />
              </Stack>
            )}
            {originalData && (
              <Box>
                <Button
                  variant="text"
                  color="error"
                  onClick={() => onDelete && onDelete(originalData)}
                  startIcon={<DeleteIcon  />}
                >
                  {t("page.orderQuoteSettings.table.button.delete")}
                </Button>
              </Box>
            )}
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ pt: 2, justifyContent: "space-between" }}>
        <Typography>{t("page.orderQuoteSettings.modal.label.mandatory")}</Typography>
        <Stack direction="row" spacing={2}>
          <Button variant="text" data-test-id="address-modal-btn-cancel" onClick={close}>
            {t("page.orderQuoteSettings.modal.button.cancel")}
          </Button>
          <Button
            data-test-id="address-modal-btn-save"
            disabled={isDisabled}
            onClick={() => {
              const values = getValues();
              const accounts = availableAccounts ?? [];
              onSubmit?.({
                recipientEmail: values.recipientEmail,
                orderAcknowledgement: values.orderAcknowledgement.map((id) => accounts.find((item) => item.id === id)!),
                quoteAcknowledgement: values.quoteAcknowledgement.map((id) => accounts.find((item) => item.id === id)!),
              });
              close();
            }}
          >
            {t(isEditMode ? "page.orderQuoteSettings.modal.button.save" : "page.orderQuoteSettings.modal.button.add")}
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );

  return { modal, open, close, visible: isModalOpen } as const;
};
